import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { transformShopifyProduct } from "@/lib/shopify-transform";
import { orpc } from "@/utils/orpc";

interface UseSearchDropdownProps {
  debouncedSearch: string;
}

export function useSearchDropdown({ debouncedSearch }: UseSearchDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["search-dropdown", debouncedSearch],
    queryFn: async () => {
      if (debouncedSearch.length < 2) {
        return [];
      }

      const response = await orpc.shopify.getShopProducts.call({
        first: 5,
        search: debouncedSearch,
        sortKey: "RELEVANCE",
      });

      return response.products.map(transformShopifyProduct);
    },
    enabled: debouncedSearch.length >= 2,
    // Optimized query configuration for smooth UX and no flickering
    staleTime: 30 * 1000, // 30 seconds - keep results fresh but avoid excessive refetching
    gcTime: 5 * 60 * 1000, // 5 minutes - cache results for better performance
    refetchOnWindowFocus: false, // Prevent refetch on window focus for dropdown
    refetchOnMount: false, // Use cached data when available
    // Use stale-while-revalidate pattern for smoother experience - prevents flickering
    placeholderData: (previousData) => previousData,
    // Reduce network waterfall and improve perceived performance
    networkMode: "online",
  });

  // Use isFetching instead of isLoading for better UX
  // isLoading is only true on first fetch, isFetching includes background updates
  const isSearching = isFetching && debouncedSearch.length >= 2;

  return {
    results,
    isLoading: isSearching,
    isOpen,
    setIsOpen,
  };
}
